import React, { useEffect, useState } from "react";
import { IAddEbook, IEbookId } from "../../Ebook.props";
import { useForm } from "react-hook-form";
import { CancelButton, FormButton } from "../../../../components/Botton";
import Loader from "../../../../components/Loader";
import useEditEbook from "./useEditEbook";

const EditEbook: React.FC<IEbookId> = ({ ebookId, setSelectedEbookId }) => {
  const {
    formData,
    setFormData,
    loading,
    languages,
    courses,
    subjectName,
    standard,
    isPhyiscalBookAvailable,
    handleChange,
    handleSubmit,
    handleCancel,
  } = useEditEbook(ebookId, setSelectedEbookId);

  const {
    register,
    handleSubmit: useFormSubmit,
    formState: { errors },
    reset,
  } = useForm<IAddEbook>();

  const [selectedStandard, setSelectedStandard] = useState<string>(formData.standard || "");
  const [selectedCourse, setSelectedCourse] = useState<string>(formData.courseName || "");
  const [filteredSubjects, setFilteredSubjects] = useState(subjectName || []);
  const [filteredCourse, setFilteredCourse] = useState(courses || []);

  // Reset form with new data when formData changes
    useEffect(() => {
      if (formData && Object.keys(formData).length > 0) {
        reset(formData);
      }
    }, [formData, reset]);

  // Set initial values when formData loads
  useEffect(() => {
    if (formData.standard) {
      setSelectedStandard(formData.standard);
    }
    if (formData.courseName) {
      setSelectedCourse(formData.courseName);
    }
  }, [formData]);

  // Update filtered subjects when standard changes or on initial load
  useEffect(() => {
    if (selectedCourse) { 
      const filtered = subjectName?.filter(
        (subject) => subject.courseName === selectedCourse
      );
      setFilteredSubjects(filtered || []);
    } else {
      setFilteredSubjects(subjectName || []);
    }

  }, [selectedCourse, subjectName]);

  useEffect(() => {
    if (selectedStandard) { 
      const filtered = courses?.filter(
        (course) => course.standard === selectedStandard
      );
      setFilteredCourse(filtered || []);
    } else {
      setFilteredCourse(courses || []);
    }
  }, [selectedStandard, courses]);

  const handleStandardChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedStandard(value);
    handleChange(e);
  };
  const handleCourseChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedCourse(value);
    handleChange(e);
  };

  const fields = [
    {
      label: "Language",
      name: "languageName",
      type: "select",
      options: languages,
    },
    { label: "Education", name: "standard", type: "select", options: standard },
    { label: "Stream", name: "courseName", type: "select", options: filteredCourse },
    {
      label: "Subject",
      name: "subject",
      type: "select",
      options: filteredSubjects,
    },
    { label: "Book Name", name: "bookName", type: "text", required: true  },
    { label: "Price", name: "eBookPrice", type: "number", required: false },
    { label: "Is Physical Book Available ?", name: 'isPhysicalBookAvailable', type: "select", options: isPhyiscalBookAvailable },
    { label: "Physical Book Price", name: 'physicalBookPrice', type: "number", required: false  },
    { label: "Description", name: "description", type: "text", required: true  },
    { label: 'Image', name: 'imageUrl', type: 'file' },
    { label: 'Pdf ', name: 'pdfUrl', type: 'file' }
  ] as const;

  return (
    <div className="flex flex-col items-center w-full h-screen p-8">
      <div className="bg-white shadow-md rounded-lg pt-6 pb-6 w-full">
        <div className="border-b-2 w-full">
          <h1 className="text-3xl mb-2 text-gray-700 ml-5">Edit Ebook</h1>
        </div>
        {loading && <Loader />}
        <form onSubmit={useFormSubmit(handleSubmit)} className="space-y-6">
          <fieldset className="m-2 rounded border-opacity-60 bg-white">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 m-4">
              {fields.map((field) => (
                <div key={field.name}>
                  <label
                    className="block text-gray-700 text-base font-semibold mb-2"
                    htmlFor={field.name}
                  >
                    {field.label}:
                  </label>
                  {field.type === "select" ? (
                    <select
                      id={field.name}
                      {...register(field.name, { required: true })}
                      onChange={
                        field.name === "standard" ? handleStandardChange :
                        field.name === "courseName" ? handleCourseChange :
                         handleChange
                      }
                      value={formData[field.name] || ""}
                      className="appearance-none border rounded w-full py-2 px-3 text-gray-600 leading-tight focus:outline-none focus:shadow-outline"
                    >
                      <option value="">Select {field.label}</option>
                      {field.options.map((option) => (
                        <option key={option._id} value={option[field.name]}>
                          {option[field.name]}
                        </option>
                      ))}
                    </select>
                  ) : field.type === "file" ? (
                    <div>
                      <input
                        className="appearance-none border rounded w-full py-2 px-3 text-gray-600 leading-tight focus:outline-none focus:shadow-outline"
                        type={field.type}
                        id={field.name}
                        {...register(field.name, { required: false })}
                        onChange={handleChange}
                      />
                      {formData[field.name] && (
                        <a
                          href={String(formData[field.name])}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:text-blue-700 text-sm mt-1 block"
                        >
                          Preview {field.label}
                        </a>
                      )}
                    </div>
                  ) : (
                    <input
                      className="appearance-none border rounded w-full py-2 px-3 text-gray-600 leading-tight focus:outline-none focus:shadow-outline"
                      type={field.type}
                      id={field.name}
                      {...register(field.name, { required: field.required })}
                      value={formData[field.name] || ""}
                      onChange={handleChange}
                    />
                  )}
                  {errors[field.name] && (
                    <span className="text-red-500 text-xs italic">
                      {field.label} field is required
                    </span>
                  )}
                </div>
              ))}
            </div>
          </fieldset>
          <div className="flex justify-start mt-6 ml-10">
            <FormButton
              className="py-2 px-4 rounded mr-4"
              type="submit"
              children="Update"
            />
            <CancelButton
              className="py-2 px-4 rounded"
              onClick={handleCancel}
              children="Cancel"
              type="button"
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditEbook;